import type { ClubDefinition, ClubId } from './Club';

export type TrajectoryId = 'low' | 'normal' | 'high';

export interface TrajectoryDefinition {
  readonly id: TrajectoryId;
  readonly label: 'LOW' | 'NORMAL' | 'HIGH';
  readonly launchDeltaDegrees: number;
  readonly speedMultiplier: number;
  readonly spinMultiplier: number;
  readonly rolloutMultiplier: number;
  readonly bounceMultiplier: number;
  readonly windExposure: number;
  readonly difficulty: number;
}

const NORMAL: TrajectoryDefinition = {
  id: 'normal',
  label: 'NORMAL',
  launchDeltaDegrees: 0,
  speedMultiplier: 1,
  spinMultiplier: 1,
  rolloutMultiplier: 1,
  bounceMultiplier: 1,
  windExposure: 1,
  difficulty: 1,
};

const TRAJECTORY_BY_CLUB: Record<ClubId, Record<TrajectoryId, TrajectoryDefinition>> = {
  driver: {
    low: {
      id: 'low',
      label: 'LOW',
      launchDeltaDegrees: -4,
      speedMultiplier: 0.97,
      spinMultiplier: 0.72,
      rolloutMultiplier: 0.72,
      bounceMultiplier: 1.18,
      windExposure: 0.62,
      difficulty: 1.05,
    },
    normal: NORMAL,
    high: {
      id: 'high',
      label: 'HIGH',
      launchDeltaDegrees: 3,
      speedMultiplier: 0.94,
      spinMultiplier: 1.15,
      rolloutMultiplier: 1.25,
      bounceMultiplier: 0.86,
      windExposure: 1.18,
      difficulty: 1.18,
    },
  },
  '3-wood': {
    low: {
      id: 'low',
      label: 'LOW',
      launchDeltaDegrees: -4.5,
      speedMultiplier: 0.96,
      spinMultiplier: 0.7,
      rolloutMultiplier: 0.7,
      bounceMultiplier: 1.18,
      windExposure: 0.58,
      difficulty: 1.05,
    },
    normal: NORMAL,
    high: {
      id: 'high',
      label: 'HIGH',
      launchDeltaDegrees: 4,
      speedMultiplier: 0.91,
      spinMultiplier: 1.18,
      rolloutMultiplier: 1.35,
      bounceMultiplier: 0.78,
      windExposure: 1.28,
      difficulty: 1.25,
    },
  },
  '5-iron': {
    low: {
      id: 'low',
      label: 'LOW',
      launchDeltaDegrees: -6,
      speedMultiplier: 0.94,
      spinMultiplier: 0.68,
      rolloutMultiplier: 0.62,
      bounceMultiplier: 1.22,
      windExposure: 0.52,
      difficulty: 1,
    },
    normal: NORMAL,
    high: {
      id: 'high',
      label: 'HIGH',
      launchDeltaDegrees: 5,
      speedMultiplier: 0.9,
      spinMultiplier: 1.25,
      rolloutMultiplier: 1.45,
      bounceMultiplier: 0.72,
      windExposure: 1.38,
      difficulty: 1.22,
    },
  },
  '7-iron': {
    low: {
      id: 'low',
      label: 'LOW',
      launchDeltaDegrees: -7,
      speedMultiplier: 0.92,
      spinMultiplier: 0.65,
      rolloutMultiplier: 0.58,
      bounceMultiplier: 1.25,
      windExposure: 0.48,
      difficulty: 0.96,
    },
    normal: NORMAL,
    high: {
      id: 'high',
      label: 'HIGH',
      launchDeltaDegrees: 7,
      speedMultiplier: 0.86,
      spinMultiplier: 1.35,
      rolloutMultiplier: 1.65,
      bounceMultiplier: 0.62,
      windExposure: 1.55,
      difficulty: 1.18,
    },
  },
  'pitching-wedge': {
    low: {
      id: 'low',
      label: 'LOW',
      launchDeltaDegrees: -10,
      speedMultiplier: 0.9,
      spinMultiplier: 0.6,
      rolloutMultiplier: 0.5,
      bounceMultiplier: 1.28,
      windExposure: 0.42,
      difficulty: 0.96,
    },
    normal: NORMAL,
    high: {
      id: 'high',
      label: 'HIGH',
      launchDeltaDegrees: 14,
      speedMultiplier: 0.72,
      spinMultiplier: 1.75,
      rolloutMultiplier: 2.6,
      bounceMultiplier: 0.38,
      windExposure: 1.9,
      difficulty: 1.35,
    },
  },
  'sand-wedge': {
    low: {
      ...NORMAL,
      id: 'low',
      label: 'LOW',
      launchDeltaDegrees: -10,
      speedMultiplier: 0.88,
      spinMultiplier: 0.62,
      rolloutMultiplier: 0.52,
      bounceMultiplier: 1.2,
      windExposure: 0.45,
      difficulty: 1,
    },
    normal: NORMAL,
    high: {
      ...NORMAL,
      id: 'high',
      label: 'HIGH',
      launchDeltaDegrees: 13,
      speedMultiplier: 0.7,
      spinMultiplier: 1.7,
      rolloutMultiplier: 2.5,
      bounceMultiplier: 0.4,
      windExposure: 1.85,
      difficulty: 1.32,
    },
  },
  'lob-wedge': {
    low: {
      ...NORMAL,
      id: 'low',
      label: 'LOW',
      launchDeltaDegrees: -9,
      speedMultiplier: 0.86,
      spinMultiplier: 0.65,
      rolloutMultiplier: 0.5,
      bounceMultiplier: 1.16,
      windExposure: 0.48,
      difficulty: 1.02,
    },
    normal: NORMAL,
    high: {
      ...NORMAL,
      id: 'high',
      label: 'HIGH',
      launchDeltaDegrees: 12,
      speedMultiplier: 0.68,
      spinMultiplier: 1.8,
      rolloutMultiplier: 2.7,
      bounceMultiplier: 0.35,
      windExposure: 1.95,
      difficulty: 1.38,
    },
  },
  putter: { low: NORMAL, normal: NORMAL, high: NORMAL },
};

export function trajectoryForClub(
  club: ClubDefinition,
  requested: TrajectoryId,
): TrajectoryDefinition {
  if (club.id === 'putter') return NORMAL;
  return TRAJECTORY_BY_CLUB[club.id][requested];
}
